import { NavLink, withRouter } from 'react-router-dom'
import React, { useState } from 'react'
import { TableVirtualized } from '../table-virtualized/table'
import { useSelector } from 'react-redux'
import { makeStyles, Typography } from '@material-ui/core'
import ReportStatus from '../report-status'
import { Tooltip } from '..'
import {
    mdiExportVariant,
    mdiCheck,
    mdiMessageProcessingOutline,
    mdiTools
} from '@mdi/js'
import Icon from '@mdi/react'
import {
    getFormattedDate,
    getReportTypeTooltipText,
    getDateTimeOfEmailSent,
    getReportTypeText
} from '../../../../../utils'
import CameraIcon from '@material-ui/icons/PhotoCameraOutlined'
import ReportEmailSender from './reports-table-email-sender'
import { UserPermissions } from 'web/src/services/permissions'
import { IsAssignedIcon } from '../'
import { REPORT_TYPES } from '../../../hooks/useReportTypes'
import { CatchReplacedIcon } from '../catch-replaced-icon'
import { WarningReplacedIcon } from '../warning-replaced-icon'

const useStyles = makeStyles(theme => ({
    disabled: {
        '&&': {
            color: theme.quaternary
        }
    },
    nameLink: {
        ...theme.link
    },
    batteryWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    wifiWrapper: {
        display: 'flex',
        flex: 1,
        justifyContent: 'flex-end'
    },
    statusWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    qrIcon: {
        cursor: 'pointer'
    },
    restoreIconButton: {
        color: theme.primary
    },
    deviceStatusShortText: {
        marginLeft: 4
    },
    reportTypeWrapper: {
        display: 'flex',
        alignItems: 'center'
    },
    cameraIcon: {
        color: theme.primary,
        marginRight: 2,
        transform: 'scale(0.8)'
    },
    assignmentRowBlocker: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: theme.background,
        opacity: 0.6
    },
    shareIcons: {
        display: 'flex'
    },
    alignIcons: {
        display: 'flex',
        marginLeft: '25px'
    }
}))

function ReportsTableComponent({
    reports,
    isLoading,
    isLoadingMore,
    onLoadMore,
    sorting,
    onSort,
    contextPage,
    showAssignmentColumn,
    history
}) {
    const {
        theme,
        locale,
        user: { original_role }
    } = useSelector(state => state.main)

    const classes = useStyles()
    const isDevicePage = contextPage === 'device'
    const isLocationPage = contextPage === 'locations'
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [reportData, setReportData] = useState({})

    const handleLinkClick = e => {
        e.stopPropagation()
    }

    const isDisabledRow = isAssigned => {
        return !isAssigned && !UserPermissions.isAllOriginalGranted()
    }

    const columns = [
        isDevicePage
            ? undefined
            : {
                  key: 'device_name',
                  label: locale.reports.deviceName,
                  sortable: true,
                  dataType: 'string',
                  width: 1.5,
                  render: ({ rowData, cellData }) => (
                      <NavLink
                          onClick={handleLinkClick}
                          className={classes.nameLink}
                          to={`/devices/${rowData.device_id}`}
                      >
                          {cellData}
                      </NavLink>
                  )
              },
        isDevicePage
            ? undefined
            : {
                  key: 'device_serial_number',
                  label: locale.reports.deviceSerial,
                  sortable: true,
                  dataType: 'string',
                  width: 1.5,
                  useCustomRenderer: false
              },
        isLocationPage || isDevicePage
            ? undefined
            : {
                  key: 'location_name',
                  label: locale.reports.location,
                  sortable: true,
                  dataType: 'string',
                  width: 1.5,
                  render: ({ rowData, cellData }) => (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                          {!rowData.is_assigned && (
                              <IsAssignedIcon
                                  theme={theme}
                                  locale={locale}
                                  isLocked
                                  isHidden={
                                      !rowData.is_assigned &&
                                      UserPermissions.isAllOriginalGranted()
                                  }
                              />
                          )}
                          <Typography noWrap variant="inherit">
                              {isDisabledRow(rowData.is_assigned) ? (
                                  <div>{rowData.location_name}</div>
                              ) : (
                                  <NavLink
                                      onClick={handleLinkClick}
                                      className={classes.nameLink}
                                      to={`/locations/${rowData.location_id}/overview`}
                                  >
                                      {rowData.location_name}
                                  </NavLink>
                              )}
                          </Typography>
                      </div>
                  )
              },
        isDevicePage
            ? undefined
            : {
                  key: 'customer_name',
                  label: locale.reports.organisation,
                  sortable: true,
                  width: 1.5,
                  dataType: 'string'
              },
        {
            key: 'report_type',
            label: locale.reports.event,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            render: ({ cellData, rowData }) => {
                const catchDescription =
                    rowData.data && rowData.data.catch_description
                        ? rowData.data.catch_description
                        : null
                const hasTomPicture = !!rowData.tom_cam_picture_id
                return (
                    <div className={classes.reportTypeWrapper}>
                        {hasTomPicture && (
                            <CameraIcon className={classes.cameraIcon} />
                        )}
                        {getReportTypeText(rowData, locale)}
                        {catchDescription && (
                            <Tooltip
                                hover
                                placement="top"
                                text={rowData.data.catch_description}
                            />
                        )}
                        {rowData.has_comments ? (
                            <div style={{ marginLeft: '5px' }}>
                                <Tooltip
                                    hover
                                    placement="top"
                                    text={locale.reports.commentsAvailable}
                                >
                                    <Icon
                                        path={mdiMessageProcessingOutline}
                                        size={0.85}
                                    />
                                </Tooltip>
                            </div>
                        ) : null}
                    </div>
                )
            }
        },
        {
            key: 'created_at',
            label: locale.reports.time,
            sortable: true,
            dataType: 'date',
            width: 1.5,
            render: ({ cellData }) => <div>{getFormattedDate(cellData)}</div>
        },
        {
            key: 'status',
            label: locale.reports.status,
            sortable: false,
            dataType: 'number',
            alignCenter: true,
            width: 1,
            render: ({ rowData }) => {
                const reportTypeTooltipText = getReportTypeTooltipText(
                    locale,
                    rowData
                )
                const isStatusIconWithTooltip = reportTypeTooltipText !== null
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {rowData.in_maintenance_mode ? (
                            <Tooltip
                                hover
                                placement="top"
                                text={locale.common.maintenanceMode}
                            >
                                <Icon
                                    path={mdiTools}
                                    size={0.8}
                                    color={theme.primary}
                                />
                            </Tooltip>
                        ) : null}
                        {rowData.replaced_type === REPORT_TYPES.catch ? (
                            <>
                                <CatchReplacedIcon
                                    size={24}
                                    style={{ marginTop: -4 }}
                                />
                                →
                            </>
                        ) : null}
                        {rowData.replaced_type ===
                        REPORT_TYPES.false_triggering ? (
                            <>
                                <WarningReplacedIcon size={24} />→
                            </>
                        ) : null}
                        <Tooltip
                            hover
                            hideTooltip={!isStatusIconWithTooltip}
                            placement="top"
                            text={getReportTypeTooltipText(locale, rowData)}
                        >
                            <ReportStatus report={rowData} theme={theme} />
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: '',
            label: locale.reports.share,
            sortable: false,
            dataType: 'number',
            alignCenter: true,
            width: 1,
            render: ({ rowData }) => {
                let date
                let time
                let p = UserPermissions.report(rowData.customer_id).read

                if (rowData.email_sent_at) {
                    let timeSent = new Date(rowData.email_sent_at)
                    date = timeSent.toLocaleDateString()
                    time = timeSent.toLocaleTimeString()
                }

                if (rowData.report_type === '2' && p) {
                    return (
                        <div
                            className={
                                rowData.email_sent_at
                                    ? classes.alignIcons
                                    : classes.shareIcons
                            }
                        >
                            <Icon
                                path={mdiExportVariant}
                                size={1}
                                color={theme.quaternary}
                                onClick={e => {
                                    e.stopPropagation()
                                    setIsModalOpen(true)
                                    setReportData(rowData)
                                }}
                            />
                            {rowData.email_sent_at ? (
                                <Tooltip
                                    hover
                                    placement="top"
                                    text={getDateTimeOfEmailSent(
                                        locale,
                                        date,
                                        time
                                    )}
                                >
                                    <Icon
                                        path={mdiCheck}
                                        size={1}
                                        color={'lightgreen'}
                                    />
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                }
            }
        },
        {
            key: 'assigment_row_blocker',
            label: '',
            sortable: false,
            dataType: 'custom',
            alignCenter: true,
            width: 0,
            render: ({ rowData, cellData }) => {
                if (isDisabledRow(rowData.is_assigned))
                    return <div className={classes.assignmentRowBlocker} />
                return null
            }
        }
    ].filter(Boolean)

    const navigateToReport = rowObj => {
        history.push(`/reports/${rowObj.rowData.id}`)
    }

    return (
        <>
            <TableVirtualized
                data={reports}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                onLoadMore={onLoadMore}
                columns={columns}
                sorting={sorting}
                onSort={onSort}
                onRowClick={navigateToReport}
            />

            {isModalOpen ? (
                <ReportEmailSender
                    setIsModalOpen={setIsModalOpen}
                    reportData={reportData}
                />
            ) : null}
        </>
    )
}

const ReportsTable = withRouter(ReportsTableComponent)
export { ReportsTable }
